import { SectionShell } from "@/components/design-system/SectionShell";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table";

const code = `<Table>
  <TableHead>
    <tr>
      <TableHeadCell>Lot #</TableHeadCell>
      <TableHeadCell>Plan</TableHeadCell>
      <TableHeadCell>Status</TableHeadCell>
    </tr>
  </TableHead>
  <TableBody>
    <TableRow href="/projects/1">
      <TableCell>
        <Link href="/projects/1">03300</Link>
      </TableCell>
      <TableCell>Renown</TableCell>
      <TableCell><Badge tone="neutral">Measuring</Badge></TableCell>
    </TableRow>
  </TableBody>
</Table>`;

export function TableSection() {
  return (
    <SectionShell
      id="table"
      title="Table"
      description={
        <>
          A real <code>&lt;table&gt;</code> for collections with columns —
          scannable multi-field records like projects, bids, or cut sheets.
          The wrapper adds a hairline border, rounded corners, and horizontal
          scrolling on narrow screens. Rows can navigate: pass{" "}
          <code>href</code> to <code>TableRow</code> and the whole row becomes
          clickable.
        </>
      }
      whenToUse={
        <ul>
          <li>Collections of similar records where several fields matter at once (lists with real columns).</li>
          <li>Index pages where rows link to a detail page.</li>
          <li>Dense data that benefits from column alignment — quantities, statuses, dates.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Key/value details of a single record — use Data table (the <code>&lt;dl&gt;</code>).</li>
          <li>Simple one-line-per-item lists — use Listings.</li>
          <li>Picking one entity from a vertical list — use List pill.</li>
        </ul>
      }
      preview={
        <Table>
          <TableHead>
            <tr>
              <TableHeadCell>Lot #</TableHeadCell>
              <TableHeadCell>Plan</TableHeadCell>
              <TableHeadCell>Community</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
            </tr>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium text-ink-display">03300</TableCell>
              <TableCell>Renown</TableCell>
              <TableCell>Crescent Cove</TableCell>
              <TableCell>
                <Badge tone="neutral">Measuring</Badge>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium text-ink-display">03301</TableCell>
              <TableCell>Aspen</TableCell>
              <TableCell>Crescent Cove</TableCell>
              <TableCell>
                <Badge tone="signal">Bid sent</Badge>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium text-ink-display">00110</TableCell>
              <TableCell>Renown</TableCell>
              <TableCell>Stonebrook</TableCell>
              <TableCell>
                <Badge tone="accent">Won</Badge>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>TableRow href</code> makes the row clickable (pointer cursor +
            hover tint) via <code>router.visit</code>; clicks on links or form
            controls inside the row are left alone.
          </li>
          <li>
            <strong>Accessibility convention</strong>: when a row has{" "}
            <code>href</code>, the lead cell should contain a real{" "}
            <code>&lt;Link&gt;</code> to the same URL so keyboard and
            middle-click navigation work.
          </li>
          <li>
            The outer wrapper scrolls horizontally (<code>overflow-x-auto</code>)
            so wide tables never stretch the page on mobile.
          </li>
          <li>
            Header cells are <code>&lt;th scope="col"&gt;</code> —
            uppercase, muted, small. Keep labels to one or two words.
          </li>
        </ul>
      }
    />
  );
}
